/**
 * Chiusura del contratto: testo della PEC, lettera PDF, registrazione dell'invio.
 *
 * Il flusso e' in tre passi e l'ordine non e' negoziabile: **prima il PDF** (che assegna il
 * protocollo), **poi l'invio** della PEC da legalmail, fuori dal portale, **poi la registrazione**
 * (che porta il contratto a Disdetto e spegne i promemoria). Il portale non invia la PEC: la firma
 * del legale rappresentante e la casella PEC stanno fuori (decisione del 2026-10-30).
 *
 * Il testo e' modificabile prima di copiarlo: il modello e' un punto di partenza, non un vincolo.
 * Il PDF invece si genera dal modello con i dati del contratto, non dal testo modificato a mano:
 * se serve cambiare la lettera, si cambiano i dati o il modello, non si ritocca il PDF.
 *
 * I dati mancanti bloccano il PDF ma non l'anteprima: si vede cosa manca e dove si corregge.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useToast } from "@shared/components/ui/Toast";
import { contrattiApi } from "../services/acquistiApi";
import { Campo, Scelta, Numero, Data, Avviso } from "./campi";
import {
  MODELLI_CHIUSURA,
  TIPO_CHIUSURA,
  opzioni,
} from "../constants/optionSet";
import { dataIt, etichetta } from "../utils/formato";
import { apriFile } from "../services/file";

const oggiIso = () => new Date().toISOString().slice(0, 10);

export default function ChiusuraContratto({
  contrattoId,
  dettaglio,
  puoScrivere,
  apriSubito = null,
  onDocumentoGenerato,
  onRegistrato,
}) {
  const { showToast } = useToast();
  const [aperto, setAperto] = useState(!!apriSubito);
  const [modello, setModello] = useState(
    ["A", "B", "C", "D"].includes(apriSubito) ? apriSubito : "",
  );
  const [opz, setOpz] = useState({
    giorniPreavvisoRecesso: "",
    dataEffetto: "",
    dataProposta: "",
    giorniRiscontro: "",
  });
  const [anteprima, setAnteprima] = useState(null);
  const [testo, setTesto] = useState("");
  const [caricamento, setCaricamento] = useState(false);
  const [inCorso, setInCorso] = useState(null); // "pdf" | "registra" | null
  const [errore, setErrore] = useState(null);
  const [dataInvio, setDataInvio] = useState(oggiIso());
  const sezione = useRef(null);

  const carica = useCallback(async () => {
    setCaricamento(true);
    setErrore(null);
    try {
      const res = await contrattiApi.chiusura(contrattoId, {
        modello: modello || undefined,
        ...opz,
      });
      setAnteprima(res);
      if (!modello) setModello(res.modello);
      setTesto(res.testo ? res.testo.corpo : "");
    } catch (e) {
      setErrore(e.data?.message || e.message || "Anteprima non disponibile");
    } finally {
      setCaricamento(false);
    }
  }, [contrattoId, modello, opz]);

  useEffect(() => {
    if (!aperto) return;
    const t = setTimeout(carica, 250);
    return () => clearTimeout(t);
  }, [aperto, carica]);

  useEffect(() => {
    if (apriSubito && sezione.current) {
      sezione.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [apriSubito]);

  const setO = (k) => (v) => setOpz((o) => ({ ...o, [k]: v }));

  const copia = async () => {
    try {
      const intero = anteprima?.testo
        ? `Oggetto: ${anteprima.testo.oggetto}\n\n${testo}`
        : testo;
      await navigator.clipboard.writeText(intero);
      showToast("Testo copiato: incollalo nella PEC", "success");
    } catch {
      showToast("Copia non riuscita: seleziona e copia a mano", "error");
    }
  };

  const generaPdf = async () => {
    setInCorso("pdf");
    setErrore(null);
    try {
      const res = await contrattiApi.chiusuraPdf(contrattoId, {
        modello,
        ...opz,
      });
      showToast(`Lettera ${res.protocollo} generata`, "success");
      onDocumentoGenerato?.();
      await carica();
      await apriFile(
        contrattiApi.documento(contrattoId, res.file.id),
        res.file.nome,
      );
    } catch (e) {
      setErrore(e.data?.message || e.message || "Generazione fallita");
    } finally {
      setInCorso(null);
    }
  };

  const registra = async () => {
    if (
      !window.confirm(
        `Confermi che la PEC «${anteprima?.testo?.oggetto ?? ""}» è stata inviata il ${dataIt(dataInvio)}? ` +
          "Il contratto passerà a Disdetto e i promemoria di disdetta si fermeranno.",
      )
    )
      return;
    setInCorso("registra");
    setErrore(null);
    try {
      await contrattiApi.registraChiusura(contrattoId, { modello, dataInvio });
      showToast("Invio registrato: il contratto è Disdetto", "success");
      onRegistrato?.();
      await carica();
    } catch (e) {
      setErrore(e.data?.message || e.message || "Registrazione fallita");
    } finally {
      setInCorso(null);
    }
  };

  const registrata = !!dettaglio?.dataDisdettaInviata;
  const giorni = anteprima?.giorniAlTermine;

  return (
    <section ref={sezione} id="chiusura" className="card space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="font-semibold text-gray-900">Chiusura del contratto</h2>
        {registrata ? (
          <span className="badge-danger">
            Disdetta inviata il {dataIt(dettaglio.dataDisdettaInviata)}
            {dettaglio.protocolloDisdetta
              ? ` · ${dettaglio.protocolloDisdetta}`
              : ""}
          </span>
        ) : dettaglio?.protocolloDisdetta ? (
          <span className="badge-warning">
            Lettera {dettaglio.protocolloDisdetta} generata, invio non ancora
            registrato
          </span>
        ) : null}
        <div className="ml-auto">
          {puoScrivere && !aperto && (
            <button
              type="button"
              className="btn-primary"
              onClick={() => setAperto(true)}
            >
              Prepara la comunicazione
            </button>
          )}
        </div>
      </div>

      <div className="text-sm text-gray-600 flex flex-wrap gap-4">
        <span>
          Disdetta entro:{" "}
          <strong>{dataIt(dettaglio?.dataDisdettaEntro)}</strong>
          {giorni != null && (
            <span className={giorni < 0 ? "text-red-700" : ""}>
              {" "}
              (
              {giorni < 0
                ? `passata da ${-giorni} giorni`
                : `fra ${giorni} giorni`}
              )
            </span>
          )}
        </span>
        <span>
          Fine contratto: <strong>{dataIt(dettaglio?.dataFine)}</strong>
        </span>
        {dettaglio?.tipoChiusura != null && (
          <span>
            Tipo:{" "}
            <strong>{etichetta(TIPO_CHIUSURA, dettaglio.tipoChiusura)}</strong>
          </span>
        )}
      </div>

      {!aperto && (
        <p className="text-xs text-gray-500">
          Prepara il testo della PEC e la lettera PDF dai dati del contratto
          (fornitore, date, articolo della clausola). La PEC si invia da
          legalmail, poi si registra qui l'invio.
        </p>
      )}

      {aperto && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Campo
              label="Modello"
              aiuto={
                anteprima?.modelloProposto
                  ? `Proposto dal portale: ${anteprima.modelloProposto}`
                  : undefined
              }
            >
              <Scelta
                valore={modello}
                onChange={setModello}
                opzioni={Object.entries(MODELLI_CHIUSURA).map(
                  ([value, label]) => ({ value, label }),
                )}
                vuoto="proposto dal portale"
              />
            </Campo>
            {modello === "C" && (
              <>
                <Campo label="Giorni di preavviso per il recesso (dal contratto)">
                  <Numero
                    valore={opz.giorniPreavvisoRecesso}
                    onChange={setO("giorniPreavvisoRecesso")}
                    min="0"
                  />
                </Campo>
                <Campo
                  label="Data di effetto del recesso"
                  aiuto="Se vuota: oggi più i giorni di preavviso."
                >
                  <Data
                    valore={opz.dataEffetto}
                    onChange={setO("dataEffetto")}
                  />
                </Campo>
              </>
            )}
            {modello === "D" && (
              <>
                <Campo
                  label="Data proposta per la risoluzione"
                  aiuto="Se vuota: «una data da concordare»."
                >
                  <Data
                    valore={opz.dataProposta}
                    onChange={setO("dataProposta")}
                  />
                </Campo>
                <Campo label="Giorni per il riscontro (default 15)">
                  <Numero
                    valore={opz.giorniRiscontro}
                    onChange={setO("giorniRiscontro")}
                    min="1"
                  />
                </Campo>
              </>
            )}
          </div>

          {errore && <Avviso tipo="errore">{errore}</Avviso>}

          {anteprima?.mancanti?.length > 0 && (
            <Avviso tipo="errore">
              <strong>Dati mancanti</strong>, la lettera non si può produrre:
              <ul className="list-disc ml-5 mt-1">
                {anteprima.mancanti.map((m) => (
                  <li key={m}>{m}</li>
                ))}
              </ul>
              <div className="mt-1 text-xs">
                I dati del contratto si correggono in questa scheda (articolo
                della clausola, date, preavviso) e si salvano; quelli del
                fornitore (PEC, sede, P.IVA) da{" "}
                <Link to="/fornitori" className="underline">
                  Fornitori
                </Link>
                .
              </div>
            </Avviso>
          )}

          {anteprima?.avvisi?.map((a) => (
            <Avviso key={a} tipo="attenzione">
              {a}
            </Avviso>
          ))}

          {anteprima?.testo && (
            <>
              <Campo label="Oggetto della PEC">
                <input
                  type="text"
                  className="input"
                  readOnly
                  value={anteprima.testo.oggetto}
                />
              </Campo>
              <Campo
                label="Testo della PEC (modificabile prima di copiarlo)"
                aiuto="Il PDF si genera dai dati del contratto, non da questo testo."
              >
                <textarea
                  className="input font-mono text-xs"
                  rows={22}
                  value={testo}
                  onChange={(e) => setTesto(e.target.value)}
                />
              </Campo>
            </>
          )}

          {caricamento && (
            <p className="text-sm text-gray-500">Aggiornamento anteprima…</p>
          )}

          <div className="flex flex-wrap items-end gap-3 border-t border-gray-100 pt-4">
            <button
              type="button"
              className="btn-secondary"
              onClick={copia}
              disabled={!anteprima?.testo}
            >
              Copia testo PEC
            </button>
            <button
              type="button"
              className="btn-secondary"
              onClick={generaPdf}
              disabled={!anteprima || anteprima.mancanti?.length > 0 || inCorso}
              title="Assegna il protocollo e archivia la lettera fra i documenti del contratto"
            >
              {inCorso === "pdf"
                ? "Generazione…"
                : anteprima?.protocollo && !registrata
                  ? `Rigenera PDF (${anteprima.protocollo})`
                  : "Scarica PDF"}
            </button>
            <div className="ml-auto flex items-end gap-2">
              <Campo label="PEC inviata il">
                <Data valore={dataInvio} onChange={setDataInvio} />
              </Campo>
              <button
                type="button"
                className="btn-primary"
                onClick={registra}
                disabled={!anteprima?.protocollo || inCorso || !modello}
                title={
                  anteprima?.protocollo
                    ? "Porta il contratto a Disdetto e ferma i promemoria"
                    : "Prima genera il PDF: assegna il protocollo"
                }
              >
                {inCorso === "registra"
                  ? "Registrazione…"
                  : registrata
                    ? "Registra un nuovo invio"
                    : "Registra invio"}
              </button>
            </div>
          </div>
          <p className="text-xs text-gray-500">
            Ordine: <strong>Scarica PDF</strong> (assegna il protocollo
            PHC-DISD-AAAA-NNN e archivia la lettera) → firma e invio della PEC
            da peralbahc@legalmail.it con il PDF allegato →{" "}
            <strong>Registra invio</strong>. Le ricevute di accettazione e
            consegna si caricano fra i documenti del contratto.
          </p>
          <button
            type="button"
            className="text-xs text-gray-500 hover:underline"
            onClick={() => setAperto(false)}
          >
            Chiudi il pannello
          </button>
        </div>
      )}
    </section>
  );
}
